/**
 * System-clipboard transport for canvas nodes.
 *
 * The editor store's `clipboardNodes` is per-mount state, so a copy died with
 * the component that made it: copy in one canvas, open another, and the paste
 * had nothing to place. This module serializes a copied selection onto the
 * SYSTEM clipboard so it survives a document boundary — another canvas, another
 * tab, another window.
 *
 * Transport is `text/plain` behind a sentinel prefix rather than a custom MIME.
 * A custom clipboard type needs `ClipboardItem` + a Blob and is unevenly
 * supported, while text/plain round-trips everywhere and — the part that
 * matters — reads back off the native `paste` event with no clipboard-read
 * permission prompt. The cost is that pasting a copied node into a text field
 * yields JSON; the sentinel at least makes it self-identifying.
 *
 * Brand tokens are BAKED here, at copy time: the payload carries literal colors
 * resolved against the SOURCE brand, so pasting into another brand's canvas
 * reproduces what you copied instead of re-theming (or rendering the magenta
 * unresolved-token fallback). The in-editor clipboard keeps its raw tokens, so
 * a same-canvas paste stays brand-linked — see `pasteClipboard` vs
 * `pasteExternalNodes` in canvaskit-editor-store.
 */
import type { SceneNode, SceneDocument } from '@/lib/scene-document';
import type { BrandResolverContext } from '@nexmail/shared-util/email-builder';

import { resolveDocColors } from './canvas-brand-resolve';

/** Prefix identifying a clipboard payload as ours. Bump the version on a shape change. */
export const CANVAS_CLIPBOARD_SENTINEL = 'nexmail/canvas-nodes:v1:';

/**
 * Refuse to serialize past this. A copied selection carries whole subtrees plus
 * `pluginData` (baked section HTML, rich-text spans), so a large artboard can
 * run to megabytes — enough to jank the copy and, in some browsers, to have the
 * write silently refused. The in-editor clipboard is unaffected, so exceeding
 * this only costs cross-document paste.
 */
export const MAX_CLIPBOARD_BYTES = 4 * 1024 * 1024;

export interface CanvasClipboardPayload {
  kind: 'nexmail/canvas-nodes';
  version: 1;
  /**
   * Identifies the copy that produced this payload. The paste side compares it
   * against its own `clipboardStamp` to tell "my own copy, still in memory"
   * (paste raw, tokens intact) from "arrived from elsewhere" (paste baked).
   */
  stamp: string;
  /** Top-level node ids, in selection order. Ids are re-minted on paste. */
  topIds: string[];
  /** Every top-level node plus its descendants, keyed by id. */
  nodes: Record<string, SceneNode>;
}

// Per-page-load salt, so two tabs that both reloaded can't mint the same stamp.
const sessionSalt = Math.random().toString(36).slice(2, 10);
let stampSeq = 0;

/** A stamp unique to one copy action, across tabs. */
export function newClipboardStamp(): string {
  stampSeq += 1;
  return `${sessionSalt}:${stampSeq}`;
}

export interface SerializeArgs {
  nodes: Record<string, SceneNode>;
  topIds: string[];
  stamp: string;
  /** Source brand, used to bake token colors to literals. */
  brand: BrandResolverContext | null | undefined;
  /** Source document dimensions (SceneDocument requires them; unused by the bake). */
  width: number;
  height: number;
}

/**
 * Serialize a copied selection for the system clipboard, with brand tokens
 * resolved to literals. Returns null when the payload exceeds
 * MAX_CLIPBOARD_BYTES, so the caller can tell the user why cross-canvas paste
 * won't work for this selection.
 */
export function serializeCanvasClipboard(args: SerializeArgs): string | null {
  const { nodes, topIds, stamp, brand, width, height } = args;
  if (topIds.length === 0) return null;

  // resolveDocColors is the same pass the renderer uses, so what lands on the
  // clipboard is what the source canvas actually displays.
  const source: SceneDocument = { version: 2, width, height, nodes, rootChildren: topIds };
  const baked = resolveDocColors(source, brand ?? null);

  const payload: CanvasClipboardPayload = {
    kind: 'nexmail/canvas-nodes',
    version: 1,
    stamp,
    topIds,
    nodes: baked.nodes,
  };

  const text = CANVAS_CLIPBOARD_SENTINEL + JSON.stringify(payload);
  if (text.length > MAX_CLIPBOARD_BYTES) return null;
  return text;
}

/**
 * Parse clipboard text back into a payload, or null when it isn't ours (the
 * overwhelmingly common case — every ordinary text paste lands here first).
 */
export function parseCanvasClipboard(
  text: string | null | undefined,
): CanvasClipboardPayload | null {
  if (!text || !text.startsWith(CANVAS_CLIPBOARD_SENTINEL)) return null;

  try {
    const parsed: unknown = JSON.parse(text.slice(CANVAS_CLIPBOARD_SENTINEL.length));
    if (!isCanvasClipboardPayload(parsed)) return null;
    return parsed;
  } catch {
    // Truncated by a clipboard manager, or hand-edited. Not ours to salvage.
    return null;
  }
}

/**
 * Structural check on parsed JSON. This crosses a trust boundary — the string
 * can be anything a user pasted — so every top id must resolve to a real node
 * before the store is handed the payload.
 */
function isCanvasClipboardPayload(v: unknown): v is CanvasClipboardPayload {
  if (!v || typeof v !== 'object') return false;
  const p = v as Partial<CanvasClipboardPayload>;
  if (p.kind !== 'nexmail/canvas-nodes' || p.version !== 1) return false;
  if (typeof p.stamp !== 'string') return false;
  if (!Array.isArray(p.topIds) || p.topIds.length === 0) return false;
  if (!p.nodes || typeof p.nodes !== 'object') return false;
  return p.topIds.every((id) => typeof id === 'string' && !!p.nodes?.[id]);
}
